import * as React from "react";
import { GVCMobx } from "../../mst/kinds/gvc";
import { notification } from "antd";
import { trySaveWithReleaseWarning } from "../../utils/releaseWarning";
import { observer } from "mobx-react-lite";
import { FormButtons } from "../../components/forms/formButtons";
import { useDetailContext } from "../../components/detail/detailContext";
import { StringMobx, StringModel } from "../../mobxDataModels/stringModel";
import { NumberModel } from "../../mobxDataModels/numberModel";
import { NGSwitch } from "../../newcomponents/switch";
import NGAlert from "../../newcomponents/alert";
import { useCleanPrompt } from "../../reactHooks/useCleanPrompt";
import { PromptContext } from "../../mobxStores/prompt/prompt";
import { NGFormElement } from "../../newcomponents/ngformelement/ngformelement";
import { NGLabelText } from "../../newcomponents/text/labelText";
import { NGLabel } from "../../newcomponents/text/label";
import { ngParseLink } from "../../utils/linkParser/linkParser";
import { Gvc } from "../../schema/types/gvc";
import { IpSet } from "../../schema/types/ipset";
import { NGKindSelect } from "../../newcomponents/select/ngkindselect";

interface Props {
  gvc: GVCMobx;
}

const LoadBalancerRaw: React.FC<Props> = ({ gvc }) => {
  const { fetchItem } = useDetailContext();

  function getDefaultIsDedicatedLoadBalancerEnabled() {
    return gvc.spec.loadBalancer?.dedicated || false;
  }
  const [isDedicatedLoadBalancerEnabled, setIsDedicatedLoadBalancerEnabled] = React.useState(
    getDefaultIsDedicatedLoadBalancerEnabled(),
  );

  function getDefaultMultiZone() {
    return gvc.spec.loadBalancer?.multiZone?.enabled || false;
  }
  const [multiZone, setMultiZone] = React.useState(getDefaultMultiZone());

  function getDefaultIpSetName() {
    if (!gvc.spec.loadBalancer?.ipSet) {
      return "";
    }
    const { isValid, name } = ngParseLink(gvc.spec.loadBalancer!.ipSet, { useInputCtx: true });
    return isValid ? name : "";
  }
  const [ipsetName, setIpsetName] = React.useState(getDefaultIpSetName());

  function getDefaultTrustedProxies() {
    return gvc.spec.loadBalancer?.trustedProxies || 0;
  }
  const trustedProxiesRef = React.useRef<StringMobx>(
    NumberModel.create({
      label: "Trusted Proxies",
      min: 0,
      max: 2,
      initialValue: getDefaultTrustedProxies(),
      isRequired: true,
    }),
  );

  function getDefaultStatus5xx() {
    return gvc.spec.loadBalancer?.redirect?.class?.status5xx || "";
  }
  const status5xxRef = React.useRef<StringMobx>(
    StringModel.create({
      label: "Status 5xx",
      initialValue: getDefaultStatus5xx(),
    }),
  );
  function getDefaultStatus401() {
    return gvc.spec.loadBalancer?.redirect?.class?.status401 || "";
  }
  const status401Ref = React.useRef<StringMobx>(
    StringModel.create({
      label: "Status 401",
      initialValue: getDefaultStatus401(),
    }),
  );

  const [isLoading, setIsLoading] = React.useState(false);
  const [isDirty, setIsDirty] = React.useState(false);
  const [isValid, setIsValid] = React.useState(false);
  const [promptWhen, setPromptWhen] = React.useState(false);

  React.useEffect(() => {
    PromptContext.setWhen(promptWhen);
  }, [promptWhen]);

  useCleanPrompt();

  React.useEffect(() => {
    let res = false;
    if (isDedicatedLoadBalancerEnabled !== getDefaultIsDedicatedLoadBalancerEnabled()) res = true;
    if (isDedicatedLoadBalancerEnabled) {
      if (multiZone !== getDefaultMultiZone()) res = true;
      if (trustedProxiesRef.current.isDirty) res = true;
      if (status5xxRef.current.isDirty) res = true;
      if (status401Ref.current.isDirty) res = true;
      if (ipsetName !== getDefaultIpSetName()) res = true;
    }
    setIsDirty(res);
  }, [
    isDedicatedLoadBalancerEnabled,
    multiZone,
    trustedProxiesRef.current.isDirty,
    status5xxRef.current.isDirty,
    status401Ref.current.isDirty,
    ipsetName,
    gvc.version,
  ]);

  React.useEffect(() => {
    let res = true;
    if (isDedicatedLoadBalancerEnabled) {
      if (!trustedProxiesRef.current.isValid) res = false;
    }
    setIsValid(res);
  }, [isDedicatedLoadBalancerEnabled, trustedProxiesRef.current.isValid, gvc.version]);

  React.useEffect(() => {
    setPromptWhen(isDirty);
  }, [isDirty]);

  async function confirm() {
    try {
      setIsLoading(true);
      const body: Partial<Gvc> = { spec: { loadBalancer: { dedicated: false } } };

      if (isDedicatedLoadBalancerEnabled) {
        body.spec!.loadBalancer!.dedicated = isDedicatedLoadBalancerEnabled;

        if (trustedProxiesRef.current.value.length > 0 && trustedProxiesRef.current.isValid) {
          body.spec!.loadBalancer!.trustedProxies = Number(trustedProxiesRef.current.value);
        }

        if (status5xxRef.current.value.length > 0 || status401Ref.current.value.length > 0) {
          if (!body.spec!.loadBalancer!.redirect) {
            body.spec!.loadBalancer!.redirect = { class: {} };
            if (status5xxRef.current.value.length) {
              body.spec!.loadBalancer!.redirect!.class!.status5xx = status5xxRef.current.value;
            } else if (getDefaultStatus5xx().length > 0) {
              body.spec!.loadBalancer!.redirect!.class!.status5xx = null as any;
            }
            if (status401Ref.current.value.length) {
              body.spec!.loadBalancer!.redirect!.class!.status401 = status401Ref.current.value;
            } else if (getDefaultStatus401().length > 0) {
              body.spec!.loadBalancer!.redirect!.class!.status401 = null as any;
            }
          }
        } else if (getDefaultStatus5xx().length > 0 || getDefaultStatus401().length > 0) {
          body.spec!.loadBalancer!.redirect = null as any;
        }

        if (ipsetName) {
          body.spec!.loadBalancer!.ipSet = ngParseLink(ipsetName, { kind: "ipset" }).absolute;
        }

        if (multiZone) {
          body.spec!.loadBalancer!.multiZone = {
            enabled: multiZone,
          };
        }
      }

      body.spec!["$replace/loadBalancer"] = body.spec!.loadBalancer;
      delete body.spec!.loadBalancer;

      await gvc.patch(body);
      setPromptWhen(false);
      trustedProxiesRef.current.confirm();
      status5xxRef.current.confirm();
      status401Ref.current.confirm();
      notification.success({
        message: "Success",
        description: "Updated GVC Locations",
      });
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
      let errorMessage = e?.response?.data?.message;
      if (!errorMessage) errorMessage = e.message;
      notification.warning({
        message: "Failed",
        description: errorMessage,
      });
      if (e.response.status === 409) {
        if (fetchItem) {
          await fetchItem();
          notification.info({
            message: "Updated Item",
            description: "Fetched the latest version of the item and discarded changes.",
          });
        }
      }
    }
  }

  async function tryConfirm() {
    await trySaveWithReleaseWarning(gvc, confirm);
  }

  async function reset() {
    setIsDedicatedLoadBalancerEnabled(getDefaultIsDedicatedLoadBalancerEnabled());
    trustedProxiesRef.current.reset();
    status5xxRef.current.reset();
    status401Ref.current.reset();
    setIpsetName(getDefaultIpSetName());
    setMultiZone(getDefaultMultiZone());
  }

  return (
    <>
      <div className="mb-4">
        <NGAlert
          type="info"
          message={`Creates a dedicated load balancer in each location and enables additional Domain features: custom ports,
            protocols and wildcard hostnames. Charges apply for each location.`}
        />
        <div className="mb-1 mt-4 flex items-center">
          <NGSwitch
            data-testid="switch-capacity-ai"
            value={isDedicatedLoadBalancerEnabled}
            onChange={setIsDedicatedLoadBalancerEnabled}
          >
            <NGLabelText>Dedicated Load Balancer</NGLabelText>
          </NGSwitch>
        </div>
      </div>
      {isDedicatedLoadBalancerEnabled ? (
        <>
          <div className="mb-4">
            <div className="mb-2">
              <NGSwitch value={multiZone} onChange={setMultiZone}>
                <NGLabelText>Multi Zone</NGLabelText>
              </NGSwitch>
            </div>
            <NGAlert
              style={{ maxWidth: 600 }}
              type="info"
              message={`When set to true, replicas will be spread evenly across all supported zones in each location. Not every location has the same number of zones.`}
            />
          </div>
          <NGFormElement
            label={"Trusted Proxies"}
            required
            value={trustedProxiesRef.current.value}
            onChange={trustedProxiesRef.current.setValue}
            name="trustedProxies"
            info={[
              "Controls the address used for request logging and for setting the X-Envoy-External-Address header.",
              "If set to 1, then the last address in an existing X-Forwarded-For header will be used in place of the source client IP address.",
              "If set to 2, then the second to last address in an existing X-Forwarded-For header will be used in place of the source client IP address. If the XFF header does not have at least two addresses or does not exist then the source client IP address will be used instead.",
              "Default value is 0.",
            ]}
            error={trustedProxiesRef.current.error}
          />
          <NGLabel size={3} className="mb-2 mt-6">
            Redirects
          </NGLabel>
          <NGFormElement
            name={"status5xx"}
            label={status5xxRef.current.label}
            value={status5xxRef.current.value}
            onChange={status5xxRef.current.setValue}
            required={status5xxRef.current.isRequired}
            error={status5xxRef.current.error}
            info={["Specify the redirect url for any 500 level status code."]}
          />
          <NGFormElement
            name={"status401"}
            label={status401Ref.current.label}
            value={status401Ref.current.value}
            onChange={status401Ref.current.setValue}
            required={status401Ref.current.isRequired}
            error={status401Ref.current.error}
            info={[
              `An optional url redirect for 401 responses.`,
              `Supports envoy format strings to include request information.`,
              `E.g. https://your-oauth-server/oauth2/authorize?return_to=%REQ(:path)%&client_id=your-client-id`,
            ]}
          />
          <NGLabel>IP Set</NGLabel>
          <NGKindSelect<IpSet>
            kind="ipset"
            className={"mb-4"}
            style={{ width: 450 }}
            optionValueAs={"name"}
            value={ipsetName}
            onChange={(value) => setIpsetName(value)}
          />
          {getDefaultIpSetName() !== ipsetName ? (
            <NGAlert
              type={"warning"}
              title={`Changing the ip set will cause a temporary outage for all endpoints in this GVC.`}
            />
          ) : null}
        </>
      ) : null}
      <FormButtons
        loading={isLoading}
        resetDisabled={isLoading || !isDirty}
        saveDisabled={isLoading || !isDirty || !isValid}
        onReset={reset}
        onSave={tryConfirm}
      />
    </>
  );
};

export const LoadBalancer = observer(LoadBalancerRaw);
